import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface WorkPanel {
  workId: number;
  minimized: boolean;
}

interface WorkPanelState {
  panels: WorkPanel[];
  focusedWorkId: number | null;
}

const STORAGE_KEY = "openWorkPanels";
const MAX_PANELS = 3;

const loadState = (): WorkPanelState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Support both old format (array) and new format (object)
      if (Array.isArray(parsed)) {
        return { panels: parsed, focusedWorkId: parsed[0]?.workId ?? null };
      }
      return parsed;
    }
  } catch (e) {
    console.error("Could not load work panels from localStorage:", e);
  }
  return { panels: [], focusedWorkId: null };
};

const saveState = (state: WorkPanelState) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ panels: state.panels, focusedWorkId: state.focusedWorkId })
    );
  } catch (e) {
    console.error("Could not save work panels to localStorage:", e);
  }
};

const initialState: WorkPanelState = loadState();

const workPanelSlice = createSlice({
  name: "workPanel",
  initialState,
  reducers: {
    openPanel(state, action: PayloadAction<number>) {
      const workId = action.payload;
      const existing = state.panels.find((p) => p.workId === workId);
      if (existing) {
        existing.minimized = false;
      } else {
        if (state.panels.length >= MAX_PANELS) {
          state.panels.pop();
        }
        state.panels.push({ workId, minimized: false });
      }
      state.focusedWorkId = workId;
      saveState(state);
    },
    closePanel(state, action: PayloadAction<number>) {
      state.panels = state.panels.filter((p) => p.workId !== action.payload);
      if (state.focusedWorkId === action.payload) {
        state.focusedWorkId = state.panels[0]?.workId ?? null;
      }
      saveState(state);
    },
    toggleMinimize(state, action: PayloadAction<number>) {
      const panel = state.panels.find((p) => p.workId === action.payload);
      if (panel) {
        panel.minimized = !panel.minimized;
        saveState(state);
      }
    },
    focusPanel(state, action: PayloadAction<number>) {
      if (state.panels.some((p) => p.workId === action.payload)) {
        state.focusedWorkId = action.payload;
        saveState(state);
      }
    },
    closeAll(state) {
      state.panels = [];
      state.focusedWorkId = null;
      saveState(state);
    },
  },
});

export const { openPanel, closePanel, toggleMinimize, focusPanel, closeAll } =
  workPanelSlice.actions;
export default workPanelSlice.reducer;
